import React from "react";
import { Helmet } from "react-helmet-async";

// Shared SEO component — every indexable page passes its copy through this so
// title, OpenGraph, Twitter Card, canonical, robots, hreflang and JSON-LD tags
// stay perfectly consistent across the site.
//
// Defaults follow the approved plan in /app/memory/SEO_PLAN.md.

const SITE = "https://driveterrain.ca";
const DEFAULT_OG_IMAGE = `${SITE}/og/driveterrain-og-1200x630.png`;

export default function Seo({
  title,
  description,
  keywords,
  canonical,             // path (e.g. "/trainers") — will be prefixed with SITE
  robots = "index, follow, max-image-preview:large",
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  twitterCard = "summary_large_image",
  geoRegion = "CA",
  geoPlacename = "Canada",
  jsonLd,                // object or array of objects
  hrefLangs = true,      // emit en-CA + fr-CA alt links for the same path
  children,
}) {
  const url = canonical ? (canonical.startsWith("http") ? canonical : `${SITE}${canonical}`) : undefined;
  const fullTitle = title;
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta name="author" content="Loriento Inc." />
      <meta name="geo.region" content={geoRegion} />
      <meta name="geo.placename" content={geoPlacename} />
      {url && <link rel="canonical" href={url} />}
      {hrefLangs && url && (
        <>
          <link rel="alternate" hrefLang="en-CA" href={url} />
          <link rel="alternate" hrefLang="fr-CA" href={url} />
          <link rel="alternate" hrefLang="x-default" href={url} />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content="DriveTerrain" />
      <meta property="og:type" content={ogType} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={ogImage} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:locale" content="en_CA" />
      <meta property="og:locale:alternate" content="fr_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLdArray.map((obj) => (
        <script key={obj["@type"] || JSON.stringify(obj).slice(0, 48)} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}

// Common JSON-LD builders reused across pages.
export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "DriveTerrain",
  legalName: "Loriento Inc.",
  url: SITE,
  logo: `${SITE}/logo512.png`,
  sameAs: [],
  contactPoint: [{
    "@type": "ContactPoint",
    email: "info@driveterrain.ca",
    contactType: "customer support",
    areaServed: "CA",
    availableLanguage: ["en", "fr"],
  }],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "DriveTerrain",
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/trainers?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};
